import { FC, useEffect, useState } from 'react'
import { ReactComponent as AiSparkleIcon } from 'src/assets/svg/ai-sparkle.svg'
import { Pagination } from 'src/components/Pagination'
import { Skeleton } from 'src/components/Skeleton'
import { SentimentBar } from 'src/charts/TopicsSubtopics/SentimentBar'
import { SentimentHoverCard } from 'src/charts/TopicsSubtopics/SentimentHoverCard'
import { ContentAnalysisV2Range } from 'src/hooks/useContentAnalysisV2Range'
import { barFillRatio } from 'src/interfaces/topics.interface'
import { useGetContentAnalysisCategoriesQuery } from 'src/reactQuery/useGetContentAnalysisCategoriesQuery'
import { useGetContentAnalysisCategoryCommentsQuery } from 'src/reactQuery/useGetContentAnalysisCategoryCommentsQuery'
import { useGetContentAnalysisCategoryInsightQuery } from 'src/reactQuery/useGetContentAnalysisCategoryInsightQuery'
import {
  ContentCategoryRow,
  ContentSentiment,
} from 'src/services/contentAnalysisApi/interfaces/contentAnalysisV2.interface'
import styles from './commentsAnalysis.module.scss'
import { FilterDropdown, FilterOption } from './FilterDropdown'
import { CommentRow } from '../CommentRow'
import { formatCount, platformLabel, shortDate, shortDateTime } from '../shared'

interface CommentsAnalysisSectionProps {
  // One id on the detail page; omitted on the list page (every analysis).
  analysisIds?: string[]
  // List page only: the "All campaigns" filter, and the campaign on each row.
  campaigns?: { id: string; name: string }[]
  // The platforms offered by the platform filter.
  platforms?: string[]
  range: ContentAnalysisV2Range
}

const COLLAPSED_LIMIT = 5
const PAGE_LIMIT = 10
const DEFAULT_PLATFORMS = [
  'facebook',
  'instagram',
  'youtube',
  'tiktok',
  'linkedin',
]

const SENTIMENT_OPTIONS: FilterOption[] = [
  { label: 'All sentiments' },
  { label: 'Positive', value: 'positive' },
  { label: 'Neutral', value: 'neutral' },
  { label: 'Negative', value: 'negative' },
]

const CategoryCard: FC<{
  active: boolean
  max: number
  onSelect: () => void
  row: ContentCategoryRow
}> = ({ active, max, onSelect, row }) => (
  <button
    aria-pressed={active}
    className={`${styles.categoryCard} ${
      active ? styles.categoryCardActive : ''
    }`}
    onClick={onSelect}
    title={row.definition || row.name}
    type="button"
  >
    <span className={styles.categoryName}>{row.name}</span>
    <span className={styles.categoryCount}>{formatCount(row.count)}</span>
    {/* The bar is the card's volume against the largest category, split by
        sentiment, the same reading as the topic tables. */}
    <SentimentHoverCard
      className={styles.categoryBar}
      sentiment={row}
      total={row.count}
      totalLabel="Total Comments"
    >
      <SentimentBar fill={barFillRatio(row.count, max)} flat sentiment={row} />
    </SentimentHoverCard>
  </button>
)

const InsightPanel: FC<{
  analysisIds?: string[]
  // List page: the analyses in scope, to name the one the insight is from.
  campaigns?: { id: string; name: string }[]
  category: string | null
}> = ({ analysisIds, campaigns, category }) => {
  const { data, isError, isLoading } =
    useGetContentAnalysisCategoryInsightQuery(analysisIds, category)
  // The stored insight is written per analysis. Across several analyses it
  // describes one of them, so it says which rather than reading as the
  // account's.
  const source = campaigns
    ? data?.analysisTitle ||
      campaigns.find((campaign) => campaign.id === data?.analysisId)?.name
    : undefined

  return (
    <aside className={styles.insight}>
      <div className={styles.insightTop}>
        <span className={styles.insightChip}>
          <AiSparkleIcon className={styles.insightChipIcon} />
          AI insight
        </span>
        {data && (
          <span className={styles.insightWeek}>
            {`Week of ${shortDate(data.weekStart)}`}
          </span>
        )}
      </div>
      {isLoading && !!category ? (
        <>
          <Skeleton height={16} width="100%" />
          <Skeleton height={16} width="90%" />
          <Skeleton height={16} width="70%" />
        </>
      ) : isError ? (
        <p className={styles.insightEmpty}>
          The insight could not be loaded. Please try again later.
        </p>
      ) : !data ? (
        <p className={styles.insightEmpty}>
          No insight for this category yet. Insights are written once a week
          from that week&apos;s comments.
        </p>
      ) : (
        <>
          {source && (
            <span className={styles.insightSource} title={source}>
              {`From ${source}`}
            </span>
          )}
          <p className={styles.insightText}>{data.insight}</p>
          {data.points.length > 0 && (
            <ul className={styles.insightPoints}>
              {data.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          )}
          <span className={styles.insightFooter}>
            {`Based on ${formatCount(data.commentCount)} comments · ${formatCount(
              data.negativeCount
            )} negative`}
          </span>
        </>
      )}
    </aside>
  )
}

/**
 * Comments Analysis: the AI's open comment categories as cards (sorted by
 * count, opening on the one with the most negative comments), the comments in
 * the selected category, and that category's stored weekly AI insight.
 */
export const CommentsAnalysisSection: FC<CommentsAnalysisSectionProps> = ({
  analysisIds,
  campaigns,
  platforms = DEFAULT_PLATFORMS,
  range,
}) => {
  const [platform, setPlatform] = useState<string | undefined>()
  const [sentiment, setSentiment] = useState<ContentSentiment | undefined>()
  const [campaignId, setCampaignId] = useState<string | undefined>()
  const [selected, setSelected] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)

  const scopedIds = campaignId ? [campaignId] : analysisIds
  const scope = { ...range, analysisIds: scopedIds, platform }
  const categories = useGetContentAnalysisCategoriesQuery(scope)
  const rows = [...(categories.data?.rows ?? [])]
    .filter((row) => row.count > 0)
    .sort((a, b) => b.count - a.count)
  const max = rows[0]?.count ?? 0
  // The user's pick while it is still on screen; otherwise the category with
  // the most negative comments, as the backend names it.
  const active = rows.some((row) => row.name === selected)
    ? selected
    : categories.data?.first &&
        rows.some((r) => r.name === categories.data?.first)
      ? categories.data.first
      : rows[0]?.name ?? null
  const activeRow = rows.find((row) => row.name === active)
  // The page belongs to one list: a new range, scope, filter or category
  // (including the fallback when the picked one drops out) starts at page 1.
  const listKey = [
    range.from,
    range.to,
    range.period,
    scopedIds?.join(','),
    platform,
    sentiment,
    active,
  ].join('|')
  const [paging, setPaging] = useState({ key: listKey, page: 1 })
  const page = paging.key === listKey ? paging.page : 1
  const setPage = (next: number) => setPaging({ key: listKey, page: next })
  const limit = expanded ? PAGE_LIMIT : COLLAPSED_LIMIT
  const comments = useGetContentAnalysisCategoryCommentsQuery(scope, {
    category: active,
    limit,
    page: expanded ? page : 1,
    sentiment,
  })
  const loadingCategories = categories.isLoading && !categories.data
  const commentsTotal = comments.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(commentsTotal / PAGE_LIMIT))
  // A refetch can shrink the list under the current page (a re-label moved
  // comments out); step back to the last page that exists.
  const pastTheEnd =
    expanded &&
    !!comments.data &&
    !comments.isPlaceholderData &&
    page > totalPages
  useEffect(() => {
    if (pastTheEnd) setPaging({ key: listKey, page: totalPages })
  }, [listKey, pastTheEnd, totalPages])

  const resetList = () => {
    setExpanded(false)
    setPage(1)
  }

  const platformOptions: FilterOption[] = [
    { label: 'All platforms' },
    ...platforms.map((value) => ({ label: platformLabel(value), value })),
  ]
  const campaignOptions: FilterOption[] = [
    { label: 'All campaigns' },
    ...(campaigns ?? []).map((campaign) => ({
      label: campaign.name,
      value: campaign.id,
    })),
  ]

  const subtitle = [
    categories.data ? `${formatCount(categories.data.total)} comments` : null,
    categories.data?.updatedAt
      ? `Updated ${shortDateTime(categories.data.updatedAt)}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ')

  const renderBody = () => {
    if (loadingCategories) {
      return (
        <div className={styles.cards}>
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton
              height={104}
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              radius={16}
              width={214}
            />
          ))}
        </div>
      )
    }
    if (categories.isError && !categories.data) {
      return (
        <div className={styles.empty}>
          Comments Analysis could not be loaded. Please try again later.
        </div>
      )
    }
    if (rows.length === 0) {
      const data = categories.data
      const filtered = !!platform || !!campaignId
      let text = 'No categorised comments in this period'
      if (filtered) text = 'No comments match these filters'
      else if (!data?.total) text = 'No comments in this period'
      else if (!data.tagged) {
        text =
          'Comments have not been sorted into categories yet. They appear here once the analysis has run.'
      }
      return <div className={styles.empty}>{text}</div>
    }

    return (
      <>
        <div className={styles.cards}>
          {rows.map((row) => (
            <CategoryCard
              active={row.name === active}
              key={row.name}
              max={max}
              onSelect={() => {
                setSelected(row.name)
                resetList()
              }}
              row={row}
            />
          ))}
        </div>

        <div className={styles.main}>
          <div className={styles.listCard}>
            <h3 className={styles.listTitle}>{active}</h3>
            <div
              className={`${styles.list} ${
                comments.isFetching && !comments.isLoading ? styles.dimmed : ''
              }`}
            >
              {comments.isLoading && !comments.data ? (
                Array.from({ length: COLLAPSED_LIMIT }).map((_, index) => (
                  <div className={styles.rowSkeleton} key={index}>
                    <Skeleton circle width={40} />
                    <Skeleton height={34} width="100%" />
                  </div>
                ))
              ) : comments.isError && !comments.data ? (
                <div className={styles.empty}>
                  Comments could not be loaded. Please try again later.
                </div>
              ) : !comments.data?.items.length ? (
                <div className={styles.empty}>
                  {sentiment
                    ? `No ${sentiment} comments in ${active}`
                    : `No comments in ${active}`}
                </div>
              ) : (
                comments.data.items.map((comment, index) => (
                  <CommentRow
                    comment={comment}
                    // The same comment key can come from two analyses.
                    key={`${comment.analysisId}|${comment.key}|${index}`}
                    showCampaign={!!campaigns}
                  />
                ))
              )}
            </div>

            {!expanded && commentsTotal > COLLAPSED_LIMIT && (
              <button
                className={styles.viewAll}
                onClick={() => setExpanded(true)}
                type="button"
              >
                {`View all ${formatCount(commentsTotal)} ${
                  activeRow?.name.toLowerCase() ?? 'comments'
                }`}
              </button>
            )}
            {expanded && (
              <div className={styles.paging}>
                {totalPages > 1 && (
                  <Pagination
                    currentPage={page}
                    onPageChange={setPage}
                    totalPages={totalPages}
                  />
                )}
                <button
                  className={styles.viewAll}
                  onClick={resetList}
                  type="button"
                >
                  Show less
                </button>
              </div>
            )}
          </div>

          <InsightPanel
            analysisIds={scopedIds}
            campaigns={campaigns}
            category={active}
          />
        </div>
      </>
    )
  }

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <div className={styles.heading}>
          <h2 className={styles.title}>Comments Analysis</h2>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>
        <div className={styles.filters}>
          <FilterDropdown
            onChange={(value) => {
              setPlatform(value)
              resetList()
            }}
            options={platformOptions}
            value={platform}
          />
          <FilterDropdown
            onChange={(value) => {
              setSentiment(value as ContentSentiment | undefined)
              resetList()
            }}
            options={SENTIMENT_OPTIONS}
            value={sentiment}
          />
          {campaigns && (
            <FilterDropdown
              onChange={(value) => {
                setCampaignId(value)
                resetList()
              }}
              options={campaignOptions}
              value={campaignId}
            />
          )}
        </div>
      </div>
      {renderBody()}
    </section>
  )
}
